import { useCallback, useRef } from 'react';

import * as Sentry from '@sentry/nextjs';
import { useSetAtom } from 'jotai';

import { showCommonErrorToastAtom } from 'src/stores/toast/atoms';
import getDeviceInfo, { isPwaMode, isSafariOrIOSBrowser } from 'src/utils/device/info';

import { useCreateManagementSession } from './useCreateManagementSession';

// Safari/Firefox/PWA는 async callback 내 window.open을 차단하므로
// 이런 환경에서는 user gesture context(동기)에서 빈 창을 먼저 열어야 한다.
// Chrome/Edge desktop은 관대하여 async 이후에도 허용하므로 pre-open을 건너뛴다.
const requiresSyncPopup = () =>
  isSafariOrIOSBrowser() || getDeviceInfo().browserName?.toLowerCase() === 'firefox' || isPwaMode();

export const useManagementSession = () => {
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);
  const windowRef = useRef<Window | null>(null);

  const mutation = useCreateManagementSession({
    onSuccess: ({ url }) => {
      if (windowRef.current && !windowRef.current.closed) {
        windowRef.current.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      windowRef.current = null;
    },
    onError: (error) => {
      windowRef.current?.close();
      windowRef.current = null;
      Sentry.captureException(error, {
        tags: { feature: 'subscription_management' },
      });
      showCommonErrorToast();
    },
  });

  const mutate = useCallback(
    (
      resolveSubscriptionId: () => string | null | Promise<string | null>,
      options?: Parameters<typeof mutation.mutate>[1]
    ) => {
      // 더블클릭 방지
      if (mutation.isPending) return;

      // 팝업 차단 브라우저에서만 동기적으로 빈 창을 선점한다.
      // 그 외(Chrome/Edge desktop)는 onSuccess의 window.open fallback을 사용하므로
      // null 반환 케이스에서 불필요한 빈 탭이 깜빡이지 않는다.
      if (requiresSyncPopup()) {
        windowRef.current = window.open('', '_blank');
      }

      Promise.resolve(resolveSubscriptionId())
        .then((id) => {
          if (id) {
            mutation.mutate(id, options);
          } else {
            windowRef.current?.close();
            windowRef.current = null;
          }
        })
        .catch(() => {
          windowRef.current?.close();
          windowRef.current = null;
          showCommonErrorToast();
        });
    },
    [mutation, showCommonErrorToast]
  );

  return { ...mutation, mutate };
};
